<template>
	<b-modal
		id="create-meeting-modal"
		@change="$emit('updateModalState', false)"
		:visible="visible"
		hide-header
		centered
		content-class="create-meeting-modal"
		size="xl"
		body-class="modal-body"
		hide-footer
		modal-class="custom-modal"
		no-close-on-backdrop
	>
		<div class="custom-stepper-container">
			<div class="custom-stepper">
				<div class="custom-stepper-step">
					<div
						:class="[
							'step-icon first',
							{ active: currentStep === 1, complete: currentStep > 1 },
						]"
					>
						1
					</div>
					<div class="step-text">
						<h1 class="title">Basic information</h1>
						<h3 class="subtitle">Fill meeting information</h3>
					</div>
				</div>
				<div class="custom-stepper-step">
					<div
						:class="[
							'step-icon',
							{ active: currentStep === 2, complete: currentStep > 2 },
						]"
					>
						2
					</div>
					<div class="step-text">
						<h1 class="title">Account</h1>
						<h3 class="subtitle">Select WebEx Account</h3>
					</div>
				</div>
			</div>
			<!-- Custom Stepper -->
		</div>
		<div class="body-container">
			<button
				@click="$emit('updateModalState', false)"
				class="close-button"
				role="button"
			>
				<img src="@/assets/images/cancel-icon.svg" />
			</button>
			<form id="webex-meeting-form">
				<div v-if="currentStep === 1" class="step1">
					<h1 class="add-content-title">
						{{ isEditMode ? "Edit" : "Create" }} Webex Meeting
					</h1>
                    <div class="form-group">
						<label for="topic" class="main-label">topic (required)</label>
						<input
							type="search"
							name="topic"
							id="topic"
							:disabled="isReadOnly"
							v-model="topic"
							class="form-input"
							placeholder="Enter your webex meeting topic..."
						/>
					</div>
					<div class="inline-group">
						<div class="col-lg-6 p-0">
							<label for="start_time" class="main-label"
								>Start Time (required)</label
							>
							<input
								type="datetime-local"
								name="start_time"
								id="start_time"
								v-model="start_time"
								class="form-input"
								:disabled="isReadOnly"
								placeholder="Meeting Date"
								:min="minStartTime"
								@change="$event.target.form && $event.target.form.reportValidity()"

							/>
						</div>
						<div class="col-lg-6">
							<label for="durationHrs" class="main-label"
								>Duration (required)</label
							>
							<div class="d-flex flex-direction-row gap-2 align-items-center">
								<div class="col">
									<select
										type="number"
										name="durationHrs"
										id="durationHrs"
										v-model="durationHrs"
										class="form-input"
										placeholder="Meeting Duration"
										min="0"
										:disabled="isReadOnly"
										max="23"
									>
										<option v-for="num in 25" :value="num - 1" :key="num - 1">
											{{ num - 1 }}
										</option>
									</select>
								</div>
								<div class="col-1">hr</div>
								<div class="col">
									<select
										name="durationMin"
										id="durationMin"
										:disabled="isReadOnly"
										class="form-input"
										v-model="durationMin"
									>
										<option :value="0">0</option>
										<option :value="15">15</option>
										<option :value="30">30</option>
										<option :value="45">45</option>
									</select>
								</div>
								<div class="col-1">min</div>
							</div>
						</div>
					</div>

				</div>
				<div v-if="currentStep === 2" class="step2">
					<div class="form-group">
						<label for="createWith" class="main-label"
							>Create Meeting using (required)</label
						>
						<v-select
							:disabled="isEditMode"
							v-model="schedule_for"
							name="createWith"
							id="createWith"
							class="form-input"
							:options="webexLiveClassOptions"
							:reduce="(option) => option.serviceCode"
							label="serviceName"
							:appendToBody="true"
							:calculate-position="withPopper"
							placeholder="Select a Webex ID"
							:title="
								isEditMode
									? 'This can not be changed. Please remove and recreate meeting if you want to change these fields.'
									: ''
							"
							v-b-tooltip.hover
							:clearable="false"
						>
						</v-select>
					</div>

					<div class="form-group mb-2">
						<div class="form-check form-switch">
							<input
								class="form-check-input"
								type="checkbox"
								role="switch"
								@change="changeSaveRecording($event)"
								:disabled="isReadOnly"
								id="saveRecording"
								v-model="saveRecording"
							/>
							<label class="form-check-label" for="saveRecording"
								>Automatically save meeting recording as lecture
								</label
							>
						</div>
					</div>
				</div>
			</form>
			<p class="text-danger custom-alert-danger" v-if="isReadOnly">Attention: The available date for this content has expired, therefore it can no longer be edited.</p>
					
			<div class="step-btn-container">
				
				<a @click="goToPreviousPage" v-if="canGoToPreviousPage" role="button" class="step-btn prev"
					><i class="fas fa-long-arrow-alt-left"></i> prev
				</a>
				<button
					@click="submitOrGoToNextPage"
					role="button"
					class="step-btn"
					:disabled="!canSubmitOrGoToNextPage || loading"
					:title="
										isReadOnly
											? 'The available date for this content has expired, therefore it can no longer be edited.'
											: ''
									"
				>
					<b-spinner v-if="loading" small></b-spinner>
					{{ stepButtonContent }} <i class="fas fa-long-arrow-alt-right"></i>
				</button>
			</div>
		</div>
	</b-modal>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { createPopper } from "@popperjs/core";
import { ContentType } from "../../../constants/content";
import { getInputDateFormat } from "../../../helpers/FormatHelpers";

export default {
	name: "CreateWebexMeetingModal",
	props: {
		visible: Boolean,
		parentContentId: String,
		mode: String,
		meetingData: Object,
		contentData: Object,
	},
	data() {
		return {
			currentStep: 1,
			totalSteps: 2,
			topic: null,
			type: "2",
			start_time: null,
			durationHrs: 1,
			durationMin: 0,
			isRecurring: false,
			allowJbh: false,
			schedule_for: null,
			saveRecording: true,
			openInSDK: false,
			isAvailableForDemo: false,
			ContentType,
		};
	},
	methods: {
		...mapActions("content", [
			"createLiveClassContent",
			"getContentById",
			"updateContent",
		]),
		submitOrGoToNextPage() {
			if (this.currentStep < this.totalSteps) {
				this.currentStep += 1;
			} else if (this.currentStep === this.totalSteps) {
				if (this.isEditMode) {
					this.updateLiveClass();
				} else {
          this.createNewLiveClass();
        }
			}
		},
		goToPreviousPage() {
			if (this.currentStep > 1) {
				this.currentStep -= 1;
			}
		},
		changeSaveRecording(e){
			if(!this.saveRecording && this.visible === true)
			{
				this.$bvModal
				.msgBoxConfirm(
					`Are you sure you want to disable automatically save recording as lecture?`,
					{
						title: "Are you sure?",
						size: "md",
						buttonSize: "md",
						okVariant: "danger",
						okTitle: "Yes",
						cancelTitle: "No",
						footerClass: "p-2",
						hideHeaderClose: false,
						centered: true,
						bodyClass: "confirm-modal-body",
					}
				)
				.then((value) => {
					// this.isConfirm = value;
					if (value) {
						this.saveRecording=false;
					}else {
						this.saveRecording=true;
					}
				})
				.catch((err) => {
					console.error(err);
					// An error occurred
				});
			}
		},
		withPopper(dropdownList, component, { width }) {
			// console.log(dropdownList, component.$refs.toggle, { width });
			dropdownList.style.width = width;
			const popper = createPopper(component.$refs.toggle, dropdownList);

			return () => popper.destroy();
		},
		webexMeetingEndDate() {
			let endDate = new Date(this.start_time);
			const durationInMinutes = this.durationHrs * 60 + this.durationMin;
			endDate = endDate.setMinutes(endDate.getMinutes() + durationInMinutes);
			return endDate;	
		},

    updateLiveClass() {
      const contentObject = {
        id: this.contentData.id,
        title: this.topic,
        availableFrom: new Date(this.start_time).toISOString(),
        availableTo: new Date(this.webexMeetingEndDate()).toISOString(),
        isAvailableForDemo: this.isAvailableForDemo,
        parentDirectoryID: this.parentContentId,
        type: "LiveClass",
        data: {
          topic: this.topic,
          duration: (this.durationHrs * 60 + this.durationMin).toString(),
          isRecordingSave: this.saveRecording.toString(),
        },
      };

      this.updateContent(contentObject).then(() => {
        this.$toasted.success("Live Class updated successfully", {
          duration: 3000,
          theme: "outline",
          position: "bottom-center",
        });
        this.$emit("updateModalState", false);
        Object.assign(this.$data, this.$options.data.call(this));
      });
      return;
    },

    createNewLiveClass() {
      const liveClass = {
        title: this.topic,
        availableFrom: new Date(this.start_time).toISOString(),
        availableTo: new Date(this.webexMeetingEndDate()).toISOString(),
        isAvailableForDemo: this.isAvailableForDemo,
        parentDirectoryID: this.parentContentId,
        type: "LiveClass",
        data: {
          servicecode: this.schedule_for,
          topic: this.topic,
          isrecurringmeeting: this.isRecurring.toString(),
          duration: (this.durationHrs * 60 + this.durationMin).toString(),
          joinbeforehost: this.allowJbh.toString(),
          openinsdk: this.openInSDK.toString(),
          registrants_confirmation_email: "false",
          registrants_email_notification: "false",
          isRecordingSave: this.saveRecording.toString(),
        },
      };

      this.createLiveClassContent(liveClass)
          .then(() => {
            this.$emit("updateModalState", false);
            Object.assign(this.$data, this.$options.data.call(this));

            this.$toasted.success("Webex Meeting created successfully", {
              theme: "outline",
              position: "bottom-center",
              duration: 3000,
            });
          })
          .catch((error) => {
            const errorKeys = Object.keys(error)
            console.log(error, "res.error");
            let message = "";

            if (errorKeys.length) {
              const errorList = error[errorKeys[0]]
              console.log(errorList,"Error");
              console.log(error[errorKeys[0]],"ErrorKeys")
              message = errorList.length ? errorList[0] : "Failed to create meeting.";
            }

            if (message) {
              this.$toasted.error(message, {
                icon: "exclamation-triangle",
                theme: "outline",
                position: "bottom-center",
                duration: 3000,
              });
            }
          });
    },

		getNowPlusFive() {
			const now = new Date()
			now.setMinutes(now.getMinutes() + 5)

			const pad = n => String(n).padStart(2, '0')

			return (
			now.getFullYear() +
			'-' +
			pad(now.getMonth() + 1) +
			'-' +
			pad(now.getDate()) +
			'T' +
			pad(now.getHours()) +
			':' +
			pad(now.getMinutes())
			)
		}
	},
	computed: {
		...mapState("content", ["loading"]),
		...mapState("appState", ["externalServicesList"]),

		minStartTime() {
			return this.getNowPlusFive()
		},
		webexLiveClassOptions() {
			const options =
			this.externalServicesList?.[this.ContentType.LIVE_CLASS] || []
			return options.filter(
			  option => option.serviceProvider.toLowerCase() === 'webex'
			)
		},
		stepButtonContent() {
			if (this.currentStep !== this.totalSteps) {
				return "Next";
			}

			if (this.isEditMode)
        return "Update";

      return "Create";
		},
		canGoToPreviousPage() {
			return this.currentStep > 1;
		},
    canSubmitOrGoToNextPage() {
      if(this.currentStep < this.totalSteps) {
        // page 1
        const isRequiredFieldsFilled = this.topic && this.start_time;

        return isRequiredFieldsFilled;
      } else {
        // page 2
        if(!this.schedule_for) {
          // when service account not selected
          return false;
        }

        if(this.isReadOnly) {
          return false;
        } else {
          return true;
        }
      }
    },
		isReadOnly() {
      const isContentEnded = this.contentData?.availableTo && new Date(this.contentData.availableTo) < new Date();
			return this.isEditMode && isContentEnded;
		},
		isEditMode() {
			return this.mode === "edit";
		}
	},
	watch: {
		visible: function () {

      if(!this.visible) {
        return;
      }

      if(!this.isEditMode) {
        this.start_time = this.getNowPlusFive();
      } else {
				this.start_time = this.contentData.availableFrom
					? getInputDateFormat(new Date(this.contentData.availableFrom), true)
					: null;

				this.topic = this.contentData.title;

        if (this.meetingData.Duration) {
					const diff =
						new Date(this.contentData.availableTo) -
						new Date(this.contentData.availableFrom);

					const hours = Math.floor(diff / (1000 * 60 * 60));
					// console.log(hours);
					const mins = (diff - hours * (1000 * 60 * 60)) / (1000 * 60);
					// var hours = Math.floor(this.meetingData.Duration / 60);
					// var mins = (this.meetingData.Duration - hours * 60) * 60;
					this.durationHrs = hours;
					this.durationMin = mins;
				}

        this.openInSDK = this.meetingData.OpenInSDK;
				this.isAvailableForDemo = this.contentData.isContentAvailableForDemo;
				this.schedule_for = this.contentData.externalService.serviceName;

        if (this.contentData.extraProperties.RecordingStatus) {
					this.isRecordingSave = this.contentData.extraProperties.RecordingStatus.toLowerCase() === "required";
					this.saveRecording = this.contentData.extraProperties.RecordingStatus.toLowerCase() === "required";
				}
			}
		},
	},

	created() {
    console.log('created');
	}

};
</script>

<style scoped>
.create-meeting-modal {
	min-height: 45.375rem;
	width: 70vw;
}

/* .custom-modal .modal-dialog {
	max-width: 70vw;
} */

.create-meeting-modal .modal-body {
	display: flex;
	flex-direction: row;
	padding: 0 !important;
	font-size: 1rem;
}

.create-meeting-modal .modal-body .custom-stepper-container {
	background: #f5f5f5;
	border-radius: 8px 0px 0px 8px;
	width: 40%;
	display: flex;
	align-items: center;
	justify-content: center;
}
.create-meeting-modal .modal-body .custom-stepper-container .custom-stepper {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: center;
	row-gap: 7.5rem;
}

.create-meeting-modal .body-container {
	display: flex;
	flex-direction: column;
	padding: 2.5rem 2.5rem 1.5rem;
	width: 60%;
}

.create-meeting-modal .add-content-title {
	font-weight: 450;
	font-size: 2rem;
	line-height: 0.75;
	margin: 0.5rem 0 2rem;
	color: #000000;
}

.create-meeting-modal .step1 {
	width: 100%;
	display: flex;
	flex-direction: column;
}

#webex-meeting-form .main-label {
	font-weight: 500;
	font-size: 1rem;
	line-height: 1.37;
	text-transform: uppercase;
	color: #727272;
	margin-bottom: 1rem;
}

#webex-meeting-form .form-group {
	display: flex;
	flex-direction: column;
	margin-bottom: 2rem;
}
#webex-meeting-form .form-input {
	border: 0;
	border-bottom: 1.5px solid #b2b2b2;
	font-size: 1rem;
	outline: none;
	padding: 0 0 0.875rem;
	/* max-width: 21.625rem; */
	width: 100%;
	border-radius: 0;
	box-shadow: none !important;
	position: relative;
	height: 40px;
}

.form-input:disabled {
	background-color: #f5f5f5 !important;
	opacity: 0.5;
}

#webex-meeting-form .form-input .form-control {
	border-radius: 0;
	box-shadow: none !important;
}

#webex-meeting-form .form-check-input {
	outline: none !important;
}

.add-desc-btn {
	/* padding: 1.5rem 2.5rem; */
	margin: 0 0 2rem;
	column-gap: 0.5rem;
	color: #838383;
	display: flex;
	flex-direction: row;
	font-size: 1rem;
	align-items: center;
}

.inline-group {
	display: flex;
	flex-direction: row;
	column-gap: 1rem;
	align-items: flex-start;
	margin-bottom: 2rem;
}

input#duration::before {
	content: "hrs";
	/* position: absolute; */
	display: flex;
	right: 0;
	top: 0;
}

.registrants-list {
	max-height: 9.375rem;
	overflow-y: auto;
}

.registrants-list .registrant {
	display: flex;
	flex-direction: row;
	align-items: center;
	column-gap: 0.75rem;
	padding: 0.75rem 0;
	font-size: 1rem;
	border-bottom: 1px solid #dcdcdc;
	color: #000000;
}

.registrants-list .registrant input,
.registrants-list .registrant label {
	margin: 0;
}

.registrants-list input[type="checkbox"],
.weekly-days-checkbox-grp input[type="checkbox"] {
	background: #ffffff;
	border: 1px solid #b2b2b2;
	box-sizing: border-box;
	box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.05);
	border-radius: 3px;
	width: 1.25rem;
	height: 1.25rem;
}
.step2 .select-all {
	margin-left: auto;
	font-size: 1rem;
	color: var(--primary-color);
}

.weekly-days-checkbox-grp {
	display: flex;
	flex-direction: row;
	align-items: center;
	column-gap: 1.25rem;
	font-size: 1rem;
}

.weekly-days-checkbox-grp .custom-control {
	display: flex;
	align-items: center;
	flex-direction: row;
	column-gap: 0.5rem;
}

.weekly-days-checkbox-grp .custom-control input,
.weekly-days-checkbox-grp .custom-control label {
	margin: 0;
}

#webex-meeting-form .status-card {
	padding: 0.875rem;
	max-width: unset;
}

#webex-meeting-form input:disabled + .status-card {
	opacity: 0.5;
	cursor: not-allowed;
}
</style>
